import {
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiClient } from '../../../lib/api';
import type { ApiFailureError } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import {
  relatednessSchema,
  type Amplification,
  type Breakdown,
  type BreakdownMetric,
  type Comments,
  type CommentTerms,
  type AudienceSource,
  type Composition,
  type Digest,
  type Localities,
  type Overview,
  type Post,
  type PostMetrics,
  type Posts,
  type Relatedness,
  type RankedPosts,
  type Sentiment,
  type SentimentSeries,
  type Territory,
  type Topics,
} from './schemas';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';

export interface Window {
  from: string;
  to: string;
}

/** Every request carries `tenant_id`, so the tenant is part of the key: an
 *  entry read for one tenant is never served to another. */
export const DATA_QUERY_KEY = (campaignId: string, tenantId: string, ...rest: unknown[]) =>
  ['data', campaignId, tenantId, ...rest] as const;

function withWindow(params: URLSearchParams, window: Window): void {
  params.set('from', window.from);
  params.set('to', window.to);
}

/** `GET /read/campaigns/{c}/terms` (`api` `routers/read/terms.py`): every
 *  tracked post's comment terms unioned over the whole campaign, distinct
 *  from `useTerms`'s single-post read though the two share a response shape. */
export function useCampaignTerms(
  campaignId: string,
  tenantId: string,
  args: { window: Window; limit?: number },
): UseQueryResult<CommentTerms, ApiFailureError> {
  const client = useApiClient();

  return useQuery<CommentTerms, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'campaign-terms', args),
    queryFn: () => {
      const params = new URLSearchParams({ tenant_id: tenantId });
      withWindow(params, args.window);
      if (args.limit !== undefined) params.set('limit', String(args.limit));
      return client.getParsed(
        routePath(GET_ROUTES.readTerms, { campaignId }, params),
        GET_ROUTES.readTerms.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '',
  });
}

/** `GET /read/campaigns/{c}/topics` (`api` `routers/read/topics.py`): the
 *  campaign's topic clusters, ranked by whichever `order` the caller asked
 *  for — the route has no default, so the query waits for one. */
export function useTopics(
  campaignId: string,
  tenantId: string,
  args: { window: Window; order: string },
): UseQueryResult<Topics, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Topics, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'topics', args),
    queryFn: () => {
      const params = new URLSearchParams({ tenant_id: tenantId, order: args.order });
      withWindow(params, args.window);
      return client.getParsed(
        routePath(GET_ROUTES.readTopics, { campaignId }, params),
        GET_ROUTES.readTopics.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '' && args.order !== '',
  });
}

export type LocalityOrder = 'reach_desc' | 'comment_volume_desc' | 'reach_delta_desc';

/** `GET /read/campaigns/{c}/localities` (`api` `routers/read/localities.py`):
 *  reach, comment volume and score per locality under the campaign's own
 *  scope reference, ranked by whichever `order` the caller asked for; the
 *  caller names no locality. */
export function useLocalities(
  campaignId: string,
  tenantId: string,
  args: { window: Window; order: LocalityOrder },
): UseQueryResult<Localities, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Localities, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'localities', args),
    queryFn: () => {
      const params = new URLSearchParams({ tenant_id: tenantId, order: args.order });
      withWindow(params, args.window);
      return client.getParsed(
        routePath(GET_ROUTES.readLocalities, { campaignId }, params),
        GET_ROUTES.readLocalities.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '',
  });
}

/** `GET /read/campaigns/{c}/ranked-posts` (`api`
 *  `routers/read/ranked_posts.py`): the campaign's posts that pulled its
 *  sentiment score down, worst first. */
export function useRankedPosts(
  campaignId: string,
  tenantId: string,
  args: { window: Window; limit?: number },
): UseQueryResult<RankedPosts, ApiFailureError> {
  const client = useApiClient();

  return useQuery<RankedPosts, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'ranked-posts', args),
    queryFn: () => {
      const params = new URLSearchParams({ tenant_id: tenantId });
      withWindow(params, args.window);
      if (args.limit !== undefined) params.set('limit', String(args.limit));
      return client.getParsed(
        routePath(GET_ROUTES.readRankedPosts, { campaignId }, params),
        GET_ROUTES.readRankedPosts.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '',
  });
}

/** `tag` is optional: omitting it returns every post and story the campaign
 *  tracks, so a campaign whose tracked accounts carry no tag still lists its
 *  rows. `related: true` keeps only the posts related to the campaign, `false`
 *  only the rest (below the threshold, not yet analysed, or ruled unrelated);
 *  omitted lists them all.
 *  `enabled: false` withholds the request altogether, for a caller still
 *  settling what tag to ask for. */
type PostsArgs = {
  tag: string;
  window: Window;
  limit?: number;
  related?: boolean;
  enabled?: boolean;
};

function postsQueryOptions(
  client: ApiClient,
  campaignId: string,
  tenantId: string,
  args: PostsArgs,
) {
  return {
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'posts', args),
    queryFn: (): Promise<Posts> => {
      const params = new URLSearchParams({ tenant_id: tenantId });
      if (args.tag !== '') params.set('tag', args.tag);
      withWindow(params, args.window);
      if (args.limit !== undefined) params.set('limit', String(args.limit));
      if (args.related !== undefined) params.set('related', String(args.related));
      return client.getParsed(
        routePath(GET_ROUTES.readPosts, { campaignId }, params),
        GET_ROUTES.readPosts.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '' && (args.enabled ?? true),
  };
}

export function usePosts(
  campaignId: string,
  tenantId: string,
  args: PostsArgs,
): UseQueryResult<Posts, ApiFailureError> {
  const client = useApiClient();
  return useQuery<Posts, ApiFailureError>(postsQueryOptions(client, campaignId, tenantId, args));
}

/** Rows per feed page: two dozen cards fill a 1440 screen twice over. */
export const FEED_PAGE = 24;

export type FeedKind = 'POST' | 'REEL' | 'STORY';

/** The campaign's posts newest first, one keyset page at a time: posts under
 *  any of `tags` (every post when empty), narrowed to `kind` and `related` by
 *  the route rather than over a full load. */
export function usePostsFeed(
  campaignId: string,
  tenantId: string,
  args: { window: Window; tags: string[]; related?: boolean; kind?: FeedKind },
): UseInfiniteQueryResult<InfiniteData<Posts, string | null>, ApiFailureError> {
  const client = useApiClient();

  return useInfiniteQuery<
    Posts,
    ApiFailureError,
    InfiniteData<Posts, string | null>,
    readonly unknown[],
    string | null
  >({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'posts-feed', args),
    initialPageParam: null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ tenant_id: tenantId, limit: String(FEED_PAGE) });
      withWindow(params, args.window);
      for (const tag of args.tags) params.append('tag', tag);
      if (args.related !== undefined) params.set('related', String(args.related));
      if (args.kind !== undefined) params.set('kind', args.kind);
      if (pageParam !== null) params.set('cursor', pageParam);
      return client.getParsed(
        routePath(GET_ROUTES.readPosts, { campaignId }, params),
        GET_ROUTES.readPosts.schema,
      );
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    // Kept past the default so Back from a post finds every page it left.
    gcTime: 30 * 60_000,
    placeholderData: keepPreviousData,
    enabled: campaignId !== '' && tenantId !== '',
  });
}

/** ASSUMED: the route is real but its body has no vendored contract yet, see
 *  `postSchema`. The window is the one the figures are read over. */
export function usePost(
  campaignId: string,
  tenantId: string,
  postId: string,
  args: { window: Window },
): UseQueryResult<Post, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Post, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'posts', postId, args),
    queryFn: () => {
      const params = new URLSearchParams({ tenant_id: tenantId });
      withWindow(params, args.window);
      return client.getParsed(
        routePath(GET_ROUTES.readPost, { campaignId, postId }, params),
        GET_ROUTES.readPost.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '' && postId !== '',
  });
}

/** Built once and shared with a caller that needs several posts' metrics at
 *  once (`useQueries`, e.g. Publicações' per-row figures) instead of
 *  reimplementing the request under a key `useQuery` would not dedupe against. */
export function postMetricsQueryOptions(
  client: ApiClient,
  campaignId: string,
  tenantId: string,
  postId: string,
  args: { window: Window; metrics?: string[] },
) {
  return {
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'post', postId, 'metrics', args),
    queryFn: (): Promise<PostMetrics> => {
      const params = new URLSearchParams({ tenant_id: tenantId });
      withWindow(params, args.window);
      if (args.metrics !== undefined) {
        for (const metric of args.metrics) {
          params.append('metric', metric);
        }
      }
      return client.getParsed(
        routePath(GET_ROUTES.postMetrics, { campaignId, postId }, params),
        GET_ROUTES.postMetrics.schema,
      );
    },
  };
}

export function usePostMetrics(
  campaignId: string,
  tenantId: string,
  postId: string,
  args: { window: Window; metrics?: string[] },
): UseQueryResult<PostMetrics, ApiFailureError> {
  const client = useApiClient();

  return useQuery<PostMetrics, ApiFailureError>({
    ...postMetricsQueryOptions(client, campaignId, tenantId, postId, args),
    enabled: campaignId !== '' && tenantId !== '' && postId !== '',
  });
}

/** The breakdown is a tuple, not `string[]`: an empty one appends no
 *  `breakdown_by` and the route refuses the request, so the type has to make
 *  `[]` unrepresentable rather than merely legal-and-empty. Shared with a
 *  caller that reads several breakdowns at once through `useQueries`. */
export function compositionQueryOptions(
  client: ApiClient,
  campaignId: string,
  tenantId: string,
  postId: string,
  args: { breakdownBy: readonly [string, ...string[]]; source?: AudienceSource },
) {
  return {
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'post', postId, 'composition', args),
    queryFn: (): Promise<Composition> => {
      const params = new URLSearchParams({ tenant_id: tenantId });
      for (const breakdown of args.breakdownBy) {
        params.append('breakdown_by', breakdown);
      }
      if (args.source !== undefined) params.set('source', args.source);
      return client.getParsed(
        routePath(GET_ROUTES.postComposition, { campaignId, postId }, params),
        GET_ROUTES.postComposition.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '' && postId !== '',
  };
}

export function useComposition(
  campaignId: string,
  tenantId: string,
  postId: string,
  args: { breakdownBy: [string, ...string[]]; source?: AudienceSource },
): UseQueryResult<Composition, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Composition, ApiFailureError>(
    compositionQueryOptions(client, campaignId, tenantId, postId, args),
  );
}

export function useTerms(
  campaignId: string,
  tenantId: string,
  postId: string,
  args: { window: Window; limit?: number },
): UseQueryResult<CommentTerms, ApiFailureError> {
  const client = useApiClient();

  return useQuery<CommentTerms, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'post', postId, 'terms', args),
    queryFn: () => {
      const params = new URLSearchParams({ tenant_id: tenantId });
      withWindow(params, args.window);
      if (args.limit !== undefined) params.set('limit', String(args.limit));
      return client.getParsed(
        routePath(GET_ROUTES.postTerms, { campaignId, postId }, params),
        GET_ROUTES.postTerms.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '' && postId !== '',
  });
}

/** Pages by cursor. Every page carries the stance counts of the whole window,
 *  so a screen reads the distribution off any page instead of summing the rows
 *  it happens to have fetched. */
export function useComments(
  campaignId: string,
  tenantId: string,
  postId: string,
  args: { window: Window; limit?: number },
): UseInfiniteQueryResult<InfiniteData<Comments, string | null>, ApiFailureError> {
  const client = useApiClient();

  return useInfiniteQuery<
    Comments,
    ApiFailureError,
    InfiniteData<Comments, string | null>,
    readonly unknown[],
    string | null
  >({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'post', postId, 'comments', args),
    initialPageParam: null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ tenant_id: tenantId });
      withWindow(params, args.window);
      if (args.limit !== undefined) params.set('limit', String(args.limit));
      if (pageParam !== null) params.set('cursor', pageParam);
      return client.getParsed(
        routePath(GET_ROUTES.postComments, { campaignId, postId }, params),
        GET_ROUTES.postComments.schema,
      );
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled: campaignId !== '' && tenantId !== '' && postId !== '',
  });
}

/** ASSUMED: the route is real but its body has no vendored contract yet, see `digestSchema`. */
export function useDigest(
  campaignId: string,
  tenantId: string,
  postId: string,
  args: { window: Window },
): UseQueryResult<Digest, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Digest, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'post', postId, 'digest', args),
    queryFn: () => {
      const params = new URLSearchParams({ tenant_id: tenantId });
      withWindow(params, args.window);
      return client.getParsed(
        routePath(GET_ROUTES.postDigest, { campaignId, postId }, params),
        GET_ROUTES.postDigest.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '' && postId !== '',
  });
}

/** Every amplifier of one post; the route derives the source from the post. */
export function useAmplification(
  campaignId: string,
  tenantId: string,
  postId: string,
  args: { window: Window },
): UseQueryResult<Amplification, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Amplification, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'post', postId, 'amplification', args),
    queryFn: () => {
      const params = new URLSearchParams({ tenant_id: tenantId, post_id: postId });
      withWindow(params, args.window);
      return client.getParsed(
        routePath(GET_ROUTES.readAmplification, { campaignId }, params),
        GET_ROUTES.readAmplification.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '' && postId !== '',
  });
}

/** `related: null` clears the override, so the post falls back to its derived
 *  classification. The screens read the flag, the reach it gates and the
 *  Recorte counts off the post and the posts list, whose keys both sit under
 *  `'posts'`; the post-scoped reads under `'post'` are left alone. */
export function useSetRelatedness(
  campaignId: string,
  tenantId: string,
): UseMutationResult<Relatedness, ApiFailureError, { postId: string; related: boolean | null }> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<Relatedness, ApiFailureError, { postId: string; related: boolean | null }>({
    mutationFn: ({ postId, related }) => {
      const path = `/campaigns/${campaignId}/posts/${postId}/relatedness`;
      return related === null
        ? client.delParsed(path, relatednessSchema)
        : client.putParsed(path, { related }, relatednessSchema);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'posts') }),
  });
}

/** Built once and shared with a caller that needs several scopes' sentiment at
 *  once (`useQueries`, e.g. Publicações' per-row figures) instead of
 *  reimplementing the request under a key `useQuery` would not dedupe against. */
export function sentimentQueryOptions(
  client: ApiClient,
  campaignId: string,
  tenantId: string,
  args: { scopeRef: string; window: Window },
) {
  return {
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'sentiment', args.scopeRef, args.window),
    queryFn: (): Promise<Sentiment> => {
      const params = new URLSearchParams({ tenant_id: tenantId, scope_ref: args.scopeRef });
      withWindow(params, args.window);
      return client.getParsed(
        routePath(GET_ROUTES.readSentiment, { campaignId }, params),
        GET_ROUTES.readSentiment.schema,
      );
    },
  };
}

export function useSentiment(
  campaignId: string,
  tenantId: string,
  args: { scopeRef: string; window: Window },
): UseQueryResult<Sentiment, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Sentiment, ApiFailureError>({
    ...sentimentQueryOptions(client, campaignId, tenantId, args),
    enabled: campaignId !== '' && tenantId !== '' && args.scopeRef !== '',
  });
}

/** Same route as `useSentiment`, at a caller-chosen `bucket` (`day` or
 *  `window`) rather than the route's own `week` default, so the response can
 *  carry a withheld, k-gated point instead of only a scored weekly one. */
export function useSentimentSeries(
  campaignId: string,
  tenantId: string,
  args: { scopeRef: string; window: Window; bucket: string },
): UseQueryResult<SentimentSeries, ApiFailureError> {
  const client = useApiClient();

  return useQuery<SentimentSeries, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'sentiment-series', args),
    queryFn: () => {
      const params = new URLSearchParams({
        tenant_id: tenantId,
        scope_ref: args.scopeRef,
        bucket: args.bucket,
      });
      withWindow(params, args.window);
      return client.getParsed(
        routePath(GET_ROUTES.readSentimentSeries, { campaignId }, params),
        GET_ROUTES.readSentimentSeries.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '' && args.scopeRef !== '',
  });
}

/** The route answers one election at one date for at least one metric. A screen
 *  that does not have them yet passes `null` or an empty list, and the query
 *  waits instead of sending a request the route refuses. */
export function useTerritory(
  campaignId: string,
  tenantId: string,
  args: {
    scopeRef: string;
    electionId: string | null;
    asOf: string | null;
    metrics: readonly string[];
  },
): UseQueryResult<Territory, ApiFailureError> {
  const client = useApiClient();
  const { scopeRef, electionId, asOf, metrics } = args;

  return useQuery<Territory, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'territory', args),
    queryFn:
      electionId === null || asOf === null
        ? skipToken
        : () => {
            const params = new URLSearchParams({
              tenant_id: tenantId,
              scope_ref: scopeRef,
              election_id: electionId,
              as_of: asOf,
            });
            for (const metric of metrics) {
              params.append('metric', metric);
            }
            return client.getParsed(
              routePath(GET_ROUTES.readTerritory, { campaignId }, params),
              GET_ROUTES.readTerritory.schema,
            );
          },
    enabled: campaignId !== '' && tenantId !== '' && scopeRef !== '' && metrics.length > 0,
  });
}

/** The route has a real, tested producer; its body omits the territory ranking
 *  `overviewSchema` still models (optionally), see `rankRowSchema`. */
export function useOverview(
  campaignId: string,
  tenantId: string,
  args: { window: Window; compareTo?: Window },
): UseQueryResult<Overview, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Overview, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'overview', args),
    queryFn: () => {
      const params = new URLSearchParams({ tenant_id: tenantId });
      withWindow(params, args.window);
      if (args.compareTo !== undefined) {
        params.set('compare_from', args.compareTo.from);
        params.set('compare_to', args.compareTo.to);
      }
      return client.getParsed(
        routePath(GET_ROUTES.readOverview, { campaignId }, params),
        GET_ROUTES.readOverview.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '',
  });
}

/** One dimension's active values with the chosen metric's series, the
 *  overview, Território and Sentimento cards' shared read. `dimensionId:
 *  null` omits `dimension_id`, so the route defaults to the campaign's own
 *  first active category — the client never names one by name. */
export function useBreakdown(
  campaignId: string,
  tenantId: string,
  args: { metric: BreakdownMetric; dimensionId: string | null; window: Window },
): UseQueryResult<Breakdown, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Breakdown, ApiFailureError>({
    queryKey: DATA_QUERY_KEY(campaignId, tenantId, 'breakdown', args),
    queryFn: () => {
      const params = new URLSearchParams({ tenant_id: tenantId, metric: args.metric });
      withWindow(params, args.window);
      if (args.dimensionId !== null) params.set('dimension_id', args.dimensionId);
      return client.getParsed(
        routePath(GET_ROUTES.campaignBreakdown, { campaignId }, params),
        GET_ROUTES.campaignBreakdown.schema,
      );
    },
    enabled: campaignId !== '' && tenantId !== '',
    // The selector lifts its default dimension asynchronously, so the first
    // read (dimension unset) is followed almost immediately by a second one
    // (the lifted default): without this the card blanks to loading between
    // the two instead of refreshing in place.
    placeholderData: keepPreviousData,
  });
}
